import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "playbook_expanded_tasks"
const MODE_KEY = "playbook_expand_all_mode"
const BULK_URL = "/admin/team_playbook/task_details/bulk"
// Silently cap a single bulk expand. If a user has paginated thousands of
// rows deep, we'd rather expand the first 500 than DDoS the server.
const EXPAND_ALL_CAP = 500

export default class extends Controller {
  static targets = ["icon", "label"]
  static outlets = ["team-playbook--task-details"]
  static values = { expanded: { type: Boolean, default: false } }

  connect() {
    if (this.#expandAllMode()) {
      this.expandedValue = true
      this.#updateUI()
      // Initial expand is driven by outlet-connected callbacks below.
    } else if (this.#hasStoredExpansions()) {
      this.expandedValue = true
      this.#updateUI()
    }
  }

  // Fires for every task-details outlet that connects — initial scan AND
  // newly-appended rows from Turbo Stream pagination. Debounced into a
  // single bulk fetch per batch.
  teamPlaybookTaskDetailsOutletConnected(_outlet, _element) {
    if (!this.#expandAllMode()) return
    this.#queueBulkExpand()
  }

  async toggle() {
    if (this.busy) return

    if (this.expandedValue) {
      this.#collapseAll()
    } else {
      await this.#expandAll()
    }
  }

  async #expandAll() {
    sessionStorage.setItem(MODE_KEY, "1")
    this.expandedValue = true
    await this.#bulkExpandPage()
  }

  #collapseAll() {
    sessionStorage.removeItem(MODE_KEY)
    this.teamPlaybookTaskDetailsOutlets.forEach((ctrl) => {
      if (ctrl.expandedValue) ctrl.collapse()
    })
    this.expandedValue = false
    this.#updateUI()
  }

  #queueBulkExpand() {
    if (this._bulkExpandTimer) return
    this._bulkExpandTimer = setTimeout(() => {
      this._bulkExpandTimer = null
      this.#bulkExpandPage()
    }, 50)
  }

  async #bulkExpandPage() {
    const candidates = this.teamPlaybookTaskDetailsOutlets.filter((ctrl) => !ctrl.expandedValue)
    if (candidates.length === 0) {
      this.#updateUI()
      return
    }

    const controllers = candidates.slice(0, EXPAND_ALL_CAP)

    const entries = controllers
      .map((ctrl) => ({ task_id: ctrl.taskIdValue }))
      .filter((e) => e.task_id)

    if (entries.length === 0) {
      this.#updateUI()
      return
    }

    this.#setBusy(true)
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
      const response = await fetch(BULK_URL, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf || ""
        },
        body: JSON.stringify({ entries })
      })

      if (response.redirected) {
        window.location.href = response.url
        return
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const htmlByTaskId = await response.json()
      controllers.forEach((ctrl) => {
        const html = htmlByTaskId[ctrl.taskIdValue]
        if (html) ctrl.applyExpandedHtml(html)
      })
    } catch (error) {
      console.error("[CollapseAll] Bulk expand failed:", error)
    } finally {
      this.#setBusy(false)
      this.#updateUI()
    }
  }

  #expandAllMode() {
    return sessionStorage.getItem(MODE_KEY) === "1"
  }

  #hasStoredExpansions() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]").length > 0
    } catch {
      return false
    }
  }

  #setBusy(busy) {
    this.busy = busy
    this.element.disabled = busy
    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle("fa-expand", !busy && !this.expandedValue)
      this.iconTarget.classList.toggle("fa-compress", !busy && this.expandedValue)
      this.iconTarget.classList.toggle("fa-spinner", busy)
      this.iconTarget.classList.toggle("fa-spin", busy)
    }
    if (this.hasLabelTarget && busy) {
      this.labelTarget.textContent = "Expanding…"
    }
  }

  #updateUI() {
    if (this.hasIconTarget) {
      this.iconTarget.classList.remove("fa-spinner", "fa-spin")
      this.iconTarget.classList.toggle("fa-compress", this.expandedValue)
      this.iconTarget.classList.toggle("fa-expand", !this.expandedValue)
    }
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = this.expandedValue ? "Collapse all" : "Expand all"
    }
  }
}
